
import React, { useState, useEffect, createContext, useContext, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { User } from '@supabase/supabase-js';
import { useNavigate, useLocation } from 'react-router-dom';
import { authService } from './authService';
import { AuthContextProps, FormType } from './types';
import CompleteProfileModal from '@/components/auth/CompleteProfileModal';
import { isProfileComplete } from '@/services/profileCompletionService';

// Create the context with default values
const AuthContext = createContext<AuthContextProps>({
  user: null,
  loading: true,
  signIn: async () => ({ error: null }),
  signUp: async () => ({ error: null }),
  signOut: async () => {},
  requestPasswordReset: async () => ({ error: null }),
  signInWithGoogle: async () => ({ error: null }),
  
  // Default values for form related props
  formType: 'login',
  email: '',
  password: '',
  setEmail: () => {},
  setPassword: () => {},
  handleLogin: async () => {},
  handleReset: async () => {},
  switchFormType: () => {},
});

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [authLoading, setAuthLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Form state
  const [formType, setFormType] = useState<FormType>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  // Profile completion state
  const [showCompleteProfileModal, setShowCompleteProfileModal] = useState(false);
  const [profileCompleteChecked, setProfileCompleteChecked] = useState(false);

  // Use useCallback to prevent unnecessary re-renders
  const handleLogin = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    console.log('handleLogin called with email:', email);
    setAuthLoading(true);
    await authService.signIn(email, password);
    setAuthLoading(false);
  }, [email, password]);

  const handleGoogleLogin = useCallback(async (e: React.MouseEvent) => {
    e.preventDefault();
    console.log('handleGoogleLogin called');
    setAuthLoading(true);
    await authService.signInWithGoogle();
    // No need to set authLoading to false here as we'll be redirected to Google
  }, []);

  const handleReset = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    setAuthLoading(true);
    await authService.requestPasswordReset(email);
    setAuthLoading(false);
  }, [email]);

  const switchFormType = useCallback((type: FormType) => {
    setFormType(type);
    // Reset form fields when switching forms
    setEmail('');
    setPassword('');
  }, []);

  // Wrap service methods to handle loading state
  const signIn = useCallback(async (email: string, password: string) => {
    setAuthLoading(true);
    const result = await authService.signIn(email, password);
    setAuthLoading(false);
    return result;
  }, []);

  const signInWithGoogle = useCallback(async () => {
    setAuthLoading(true);
    const result = await authService.signInWithGoogle();
    return result;
  }, []);

  const signUp = useCallback(async (
    email: string, 
    password: string, 
    nickname: string, 
    role: string,
    preferredGame: string,
    whatsappNumber?: string
  ) => {
    setAuthLoading(true);
    const result = await authService.signUp(email, password, nickname, role, preferredGame, whatsappNumber);
    setAuthLoading(false);
    return result;
  }, []);

  const signOut = useCallback(async () => {
    setAuthLoading(true);
    await authService.signOut();
    setAuthLoading(false);
  }, []);

  const requestPasswordReset = useCallback(async (email: string) => {
    setAuthLoading(true);
    const result = await authService.requestPasswordReset(email);
    setAuthLoading(false);
    return result;
  }, []);

  // Check if profile is complete and show modal if needed
  const checkProfileCompletion = useCallback(async (userId: string) => {
    try {
      console.log('🔍 [AuthProvider] Verificando completude do perfil para:', userId);
      
      const complete = await isProfileComplete(userId);
      
      console.log('📊 [AuthProvider] Perfil está completo:', complete);
      
      if (!complete) {
        console.log('⚠️ [AuthProvider] Perfil incompleto, mostrando modal');
        setShowCompleteProfileModal(true);
      }
      
      setProfileCompleteChecked(true);
    } catch (error) {
      console.error('❌ [AuthProvider] Erro ao verificar completude do perfil:', error);
      setProfileCompleteChecked(true);
    }
  }, []);

  // Handle profile completion
  const handleProfileComplete = useCallback(() => {
    console.log('✅ [AuthProvider] Perfil completado com sucesso');
    setShowCompleteProfileModal(false);
    setProfileCompleteChecked(true);
  }, []);

  useEffect(() => {
    let isMounted = true;
    console.log('AuthProvider mounting, setting up auth state listener');
    
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        console.log('🔄 Auth state change event:', event);
        console.log('👤 Session user:', session?.user?.email || 'none');
        console.log('📊 Session metadata:', session?.user?.user_metadata);
        console.log('🌐 Current path:', location.pathname);
        
        if (isMounted) {
          setUser(session?.user ?? null);
          setLoading(false);
        }

        // Only redirect if not on page refresh (INITIAL_SESSION)
        if (event === 'SIGNED_OUT' && isMounted) {
          // Always navigate to login after sign out
          console.log('🚪 User signed out, redirecting to login...');
          setTimeout(() => {
            if (isMounted) navigate('/login');
          }, 0);
        } else if (event === 'SIGNED_IN' && isMounted) {
          // When signing in, only navigate to home if on auth page
          console.log('✅ User signed in, checking if should redirect...');
          setTimeout(() => {
            if (isMounted) {
              const isAuthPage = ['/login', '/cadastro', '/recuperar-senha'].includes(location.pathname);
              const isCallbackPage = location.pathname === '/auth/callback';
              
              if (isAuthPage && !isCallbackPage) {
                console.log('🏠 Redirecting from auth page to home...');
                navigate('/');
              } else if (isCallbackPage) {
                console.log('📞 On callback page, letting callback handle redirect...');
              } else {
                console.log('📍 Staying on current page:', location.pathname);
              }
            }
          }, 0);
        }
        // Don't redirect for INITIAL_SESSION event (page refresh)
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      console.log('Existing session check:', session ? 'Session found' : 'No session');
      if (isMounted) {
        setUser(session?.user ?? null);
        setLoading(false);
      }
    });

    return () => {
      console.log('AuthProvider unmounting, cleaning up subscription');
      isMounted = false;
      subscription.unsubscribe();
    };
  }, [navigate, location.pathname]);

  // Check profile completion when user is logged in
  useEffect(() => {
    if (user && !loading && !profileCompleteChecked) {
      // Only check on specific pages where it's relevant
      const isRelevantPage = !location.pathname.startsWith('/login') && 
                            !location.pathname.startsWith('/cadastro') && 
                            !location.pathname.startsWith('/auth/callback');
      
      if (isRelevantPage) {
        checkProfileCompletion(user.id);
      } else {
        setProfileCompleteChecked(true);
      }
    }
    
    // Reset profile check when user logs out
    if (!user) {
      setProfileCompleteChecked(false);
      setShowCompleteProfileModal(false);
    }
  }, [user, loading, profileCompleteChecked, location.pathname, checkProfileCompletion]);

  const value = React.useMemo(() => ({
    user,
    loading: loading || authLoading,
    signIn,
    signUp,
    signOut,
    requestPasswordReset,
    signInWithGoogle,
    
    // Form related values
    formType,
    email,
    password,
    setEmail,
    setPassword,
    handleLogin,
    handleReset,
    switchFormType,
    handleGoogleLogin,
  }), [
    user, loading, authLoading,
    signIn, signUp, signOut, requestPasswordReset, signInWithGoogle,
    formType, email, password, 
    handleLogin, handleReset, switchFormType, handleGoogleLogin
  ]);

  console.log('AuthProvider rendering with loading:', loading || authLoading);
  return (
    <AuthContext.Provider value={value}>
      {children}
      {/* Modal de completar perfil */}
      {user && (
        <CompleteProfileModal
          isOpen={showCompleteProfileModal}
          onClose={() => setShowCompleteProfileModal(false)}
          userId={user.id}
          onComplete={handleProfileComplete}
        />
      )}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
